import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Lock, ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { FullBrandLogo } from '../components/Logo';
import { API } from '../config/api';

export default function ForceResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { access_token, email } = location.state || {};
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  if (!access_token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="glass-card border-border p-6 text-center max-w-md">
          <ShieldCheck className="w-12 h-12 text-primary mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">No Reset Required</h2>
          <p className="text-muted-foreground mb-4">You don't need to reset your password.</p>
          <Link to="/login">
            <Button className="bg-primary hover:bg-primary/90">Go to Login</Button>
          </Link>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/force-change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token, new_password: newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Password changed! You can now log in.');
        navigate('/login');
      } else {
        toast.error(data.detail || 'Failed to change password');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="force-reset-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <FullBrandLogo size="lg" className="drop-shadow-xl" />
          </Link>
          <h1 className="font-heading text-3xl font-bold uppercase mb-2">Set New Password</h1>
          <p className="text-muted-foreground">
            {email ? `Your temporary password for ${email} must be changed.` : 'Please set a new password to continue.'}
          </p>
        </div>

        <Card className="glass-card border-border p-6">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/10 border border-primary/20 mb-6">
            <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0" />
            <p className="text-xs text-muted-foreground">
              For security, you must change your temporary password before accessing your account.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="new-password"
                  type="password"
                  placeholder="At least 8 characters"
                  className="pl-10"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  data-testid="new-password-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Re-enter password"
                  className="pl-10"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 h-11 font-semibold uppercase tracking-wider"
              disabled={loading}
              data-testid="force-reset-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <span className="flex items-center gap-2">Set Password <ArrowRight className="w-4 h-4" /></span>
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
